import moment from 'moment';
import { orderFilterOptions, savedFilterToAppliedFilterMap } from '../../../containers/orders-filter-bar-container/filter-options/options/';
import {
  isNullIndicator,
  isNotNullIndicator,
  containsIndicator,
  notContainsIndicator,
} from '../../../services/filtering/collection-filter-mapping-service';
import { GuidService } from 'ui-components/services/guid-service';
import { OptionFormTypes } from '../../../services/filtering/filter-form-render-service/option-form-types';

import {
  CurrentDate,
  WithinPastXDays,
  WithinNextXDays,
  BeforeXDays,
  AfterXDays,
  OnOrAfter,
  OnOrBefore,
} from './date-time-filter-operators';
import { noop } from 'ui-components/default-props/noop';

export const createCustomFilterView = (activeFilterView, appliedFilters) => {
  return {
    gridConfigTypeId: activeFilterView.gridConfigTypeId,
    gridViewId: activeFilterView.gridViewId,
    filterViewId: 'custom',
    appliedFilters,
  };
};

const defaultFilterView = {
  filterViewId: 'default',
  appliedFilters: [],
};

export const getDefaultFilterView = () => {
  return defaultFilterView;
};

export const updateCustomViewProp = (propName, propValue, customView) => {
  return {
    ...customView,
    [propName]: propValue,
  };
};

export const replaceUpdatedFilterView = (propName, propValue, filterViewId, savedFilterViews) => {
  const index = savedFilterViews.findIndex((view) => view.filterViewId === filterViewId);
  if (index === -1) {
    return [...savedFilterViews];
  }

  const filterView = savedFilterViews.find((view) => view.filterViewId === filterViewId);
  const updatedFilterView = {
    ...filterView,
    [propName]: propValue,
  };
  return [...savedFilterViews.slice(0, index), updatedFilterView, ...savedFilterViews.slice(index + 1)];
};

const parseFilterValue = (value) => {
  if (typeof value === 'string' && value.includes(';')) {
    return value.split(';').reduce((values, current) => {
      if (!current.length) {
        return values;
      }
      return [...values, current.trim()];
    }, []);
  }
  return value;
};

const typesThatNeedSemicolonParsing = [
  OptionFormTypes.TYPE_STRING,
  OptionFormTypes.TYPE_CURRENCY_COMPARISON,
  OptionFormTypes.TYPE_NUMBER,
  OptionFormTypes.TYPE_NUMBER_COMPARISON,
];

const mapFilters = (filters, metadata = {}) => {
  return filters.reduce((all, filter) => {
    const { column, value, operator } = filter;
    const appliedFilterMeta = savedFilterToAppliedFilterMap[column];
    if (!appliedFilterMeta) {
      return all;
    }
    const { id, filterValue, type, mapV2SavedFilterToV3Applied = noop } = appliedFilterMeta;
    const criterionArgumentValue = typesThatNeedSemicolonParsing.includes(type) ? parseFilterValue(value) : value;

    const guid = GuidService.newGuid();
    const appliedFilter = {
      id: guid,
      filterById: id,
      type,
      filterValue,
      criterionOperatorValue: operator,
      criterionArgumentValue,
      ...mapV2SavedFilterToV3Applied(filter, metadata),
    };
    return [...all, appliedFilter];
  }, []);
};

const getCollectionFilterV3Operator = (savedFilter) => {
  const { isIn, isNull } = savedFilter;
  // isNull is nullable. isIn is not. if isNull is actually null, then it is a "isIn" filter.
  if (isNull !== null) {
    return isNull ? isNullIndicator : isNotNullIndicator;
  }
  return isIn ? containsIndicator : notContainsIndicator;
};

const mapCollectionFilters = (filters) => {
  return filters.reduce((all, current) => {
    const { column, values: uncheckedValues } = current;
    const values = uncheckedValues === null ? [null] : uncheckedValues; // collection filters can have a null value
    const appliedFilterMeta = savedFilterToAppliedFilterMap[column];
    if (!appliedFilterMeta) {
      return all;
    }
    const { id, filterValue, type, mapV2SavedFilterToV3Applied = noop } = appliedFilterMeta;

    const guid = GuidService.newGuid();
    const mapped = values.map((value) => ({
      id: guid,
      filterById: id,
      type,
      filterValue,
      criterionOperatorValue: getCollectionFilterV3Operator(current),
      criterionArgumentValue: value,
      ...mapV2SavedFilterToV3Applied(value, column),
    }));
    return [...all, ...mapped];
  }, []);
};

const dateYesterday = 'dateYesterday';
const dateCustomRange = 'dateCustomRange';

const savedFilterOperatorToAppliedFilterOperatorMap = {
  [CurrentDate]: 'dateToday',
  [OnOrAfter]: 'dateOnOrAfter',
  [OnOrBefore]: 'dateOnOrBefore',
  [WithinPastXDays]: 'dateWithinThePastXDays',
  [WithinNextXDays]: 'dateWithinTheNextXDays',
  [BeforeXDays]: 'dateXDaysAgoOrMore',
  [AfterXDays]: 'dateXDaysAheadOrMore',
};

const formatValueOrDateForAppliedFilter = (value) => {
  if (value != null && value.length >= 19) {
    // using single = here to check for null and/or undefined. and checking for the expected length of a ISO date time string.
    return moment(value).format('MM/DD/YYYY');
  }
  return value;
};

const buildDateTimeFilter = (filterA, filterB) => {
  const { operator, value } = filterA;
  const formattedDateA = formatValueOrDateForAppliedFilter(value);

  if (filterB) {
    const { operator: operatorB, value: valueB } = filterB;
    const formattedDateB = formatValueOrDateForAppliedFilter(valueB);
    if ([operator, operatorB].every((op) => op === OnOrAfter || op === OnOrBefore)) {
      const criterionArgumentValue =
        operator === OnOrAfter
          ? { startRangeDate: formattedDateA, endRangeDate: formattedDateB }
          : { startRangeDate: formattedDateB, endRangeDate: formattedDateA };
      return {
        criterionOperatorValue: dateCustomRange,
        criterionArgumentValue,
      };
    }
    if (operator === CurrentDate && operatorB === WithinPastXDays) {
      return {
        criterionOperatorValue: dateYesterday,
      };
    }
  }
  if (operator === CurrentDate) {
    return {
      criterionOperatorValue: savedFilterOperatorToAppliedFilterOperatorMap.CurrentDate,
    };
  }
  if (operator === OnOrAfter || operator === OnOrBefore) {
    return {
      criterionOperatorValue: savedFilterOperatorToAppliedFilterOperatorMap[operator],
      criterionArgumentValue: formattedDateA,
    };
  }
  return {
    criterionOperatorValue: savedFilterOperatorToAppliedFilterOperatorMap[operator],
  };
};

const mapDateTimeFilters = (filters) => {
  return filters.reduce((all, current, index) => {
    const { column, value, operator } = current;
    const appliedFilterMeta = savedFilterToAppliedFilterMap[column];
    if (!appliedFilterMeta) {
      return all;
    }
    const { id, filterValue, type } = appliedFilterMeta;
    const next = filters[index + 1];
    const paired = next && next.column === current.column;

    const mapped = {
      filterById: id,
      type,
      filterValue,
      criterionOperatorValue: operator,
      criterionArgumentValue: value,
      ...buildDateTimeFilter(current, paired && next),
    };

    return [...all, mapped];
  }, []);
};

export const mapSavedFilterSetToAppliedFilters = (state, filterSetToApply = {}, metadata = {}) => {
  const appliedFilters = Object.keys(filterSetToApply).reduce((combined, currentKey) => {
    const currentFilterSubset = filterSetToApply[currentKey];

    // Check if filters are already in v3 format (have filterById instead of column)
    const isV3Format = Array.isArray(currentFilterSubset) && currentFilterSubset.length > 0 && currentFilterSubset[0].filterById;

    // If already in v3 format (e.g., from card clicks), pass through directly
    if (isV3Format) {
      return [...combined, ...currentFilterSubset];
    }

    // Otherwise, transform from v2 to v3 format
    switch (currentKey) {
      case 'collectionFilters':
        return [...combined, ...mapCollectionFilters(currentFilterSubset)];
      case 'dateTimeFilters':
        return [...combined, ...mapDateTimeFilters(currentFilterSubset)];
      default:
        return [...combined, ...mapFilters(currentFilterSubset, metadata)];
    }
  }, []);

  return {
    ...state,
    defaultFilterView: {
      ...state.defaultFilterView,
      appliedFilters,
    },
  };
};

const sanitizeViewFilter = (filter) => {
  const { filterValue } = filter;
  const { sanitizeSavedViewFilter } = orderFilterOptions.find((fO) => fO.filterValue === filterValue) || {};
  return sanitizeSavedViewFilter ? sanitizeSavedViewFilter(filter) : filter;
};

export const sanitizeV3GridConfigs = (savedFilterViews) => {
  return savedFilterViews
    .filter((view) => view.gridConfigTypeId === 'order')
    .reduce((views, view) => {
      const { appliedFilters } = view;
      const sanitizedView = {
        ...view,
        appliedFilters: (appliedFilters || []).map(sanitizeViewFilter),
      };
      return [...views, sanitizedView];
    }, []);
};
